/**
 * The SIX views PlanHub ships, in one list.
 *
 * 🔴 IT SAID "FOUR" UNTIL 2026-08-17, AND THAT SENTENCE WAS THE BUG (PH-26). `SpacesPage` and
 * `RecurrencePage` were finished features with passing suites and NO ROUTE — `/spaces` and
 * `/recurrence` both redirected to `/backlog`, so no user could reach either. A list that describes
 * itself as complete is the thing that stops anybody asking whether it is.
 *
 * ⭐ ONE declaration drives three renderings — the desktop tab row, the mobile bottom bar, and the
 * document title. A second hand-written copy of "Backlog · List · Board · Versions" is how a view
 * ends up reachable from the phone and invisible on the desktop (or the reverse), and nobody notices
 * because each surface is only ever looked at on the machine it was built on.
 *
 * ⛔⛔ `/settings` IS DELIBERATELY NOT IN THIS LIST, and `views.test.ts` pins that.
 *   Every write route behind it is `[requireUser, requireAdmin]` in the API's `routes/settings.ts` —
 *   statuses, types, priorities, impacts, resolutions and the transition matrix are GLOBAL, one set
 *   for the whole installation, so a non-admin who opens it can save nothing. This list drives the
 *   desktop tab row AND the 390px bottom bar, both of which EVERY signed-in user sees. A tab that
 *   leads most of the team to a screen where every button 403s is worse than no tab — the same
 *   judgement `/admin` already carries in `TopBar.tsx`, and `/settings` is reached the same way:
 *   an entry on the admin screen, a real route, and an explanation inside the page for anyone who
 *   types the URL. ⛔ Never a redirect: a redirect says "no such page", which is false.
 *
 * ⛔ Jira's reference screenshot has twelve entries (Summary · Timeline · Backlog · Active sprints ·
 * Calendar · Reports · List · More 9…). We ship six. Copying the SHAPE of that row is the point;
 * copying its density would put nine tabs behind a "More" on a 390px screen, which is the same as
 * not shipping them.
 */
export interface ViewDef {
  /** The route path, which is also the id. */
  path: string;
  label: string;
  /**
   * Decoration only — every tab carries its text label on every breakpoint, including the 390px
   * bottom bar. ⛔ An icon-only tab bar is a memory test.
   */
  glyph: string;
  /** Used as the tab's title attribute and by the placeholder, so "what is this view" has one answer. */
  description: string;
}

export const VIEWS: readonly ViewDef[] = [
  {
    path: '/backlog',
    label: 'Backlog',
    glyph: '☰',
    description: 'The ranked backlog, with sub-tasks nested under their parents.',
  },
  {
    path: '/list',
    label: 'List',
    glyph: '▤',
    description: 'Every ticket as a flat, sortable table with configurable columns.',
  },
  {
    path: '/board',
    label: 'Board',
    glyph: '▦',
    description: 'Statuses as columns, grouped into lanes.',
  },
  {
    path: '/versions',
    label: 'Versions',
    glyph: '⚑',
    description: 'Releases per project, and the tickets fixed in each.',
  },
  /**
   * ⭐ A MAIN-NAV TAB, not an admin one. Every write behind this screen is open to any signed-in
   * member — `POST /spaces/:id/items` and `POST /space-items/:id/promote` refuse only NON-MEMBERS,
   * the identical rule the board and the backlog already live under. It is a work surface, so it
   * belongs beside the work surfaces.
   */
  {
    path: '/spaces',
    label: 'Spaces',
    glyph: '◈',
    description: 'Meeting topics and research items — not tickets, until one is promoted.',
  },
  /**
   * ⭐ ALSO A MAIN-NAV TAB, and the one with the strongest case for being one tap away: its default
   * tab is the MISSED list, which is the answer to "was the weekly duty actually done?" across every
   * project. ⛔ Burying the only detector this product has behind an admin screen would make it
   * something somebody has to remember to go and look at, which is the failure db/002 replaced.
   */
  {
    path: '/recurrence',
    label: 'Recurring',
    glyph: '↻',
    description: 'Recurring duties, the periods they expect, and the ones that were missed.',
  },
];

/**
 * The route every unknown path falls back to.
 * ⚠️ It used to be described as "the one view that is actually finished". All six are now built and
 * routed; the backlog is the default because it is where work is planned, not because it is the only
 * thing that renders.
 */
export const DEFAULT_VIEW_PATH = '/backlog';

export function viewForPath(pathname: string): ViewDef | null {
  return VIEWS.find((view) => view.path === pathname) ?? null;
}

/**
 * ⭐ Throws rather than falling back to a plausible default. A route whose view was deleted from
 * this list is a wiring mistake; rendering it with an invented label would hide the mistake behind
 * something that looks fine.
 */
export function requireView(pathname: string): ViewDef {
  const view = viewForPath(pathname);
  if (!view) throw new Error(`No view is registered for "${pathname}" — see views.ts`);
  return view;
}
